import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import Transition from 'react-transition-group/Transition';
import FocusTrap from 'focus-trap-react';

import * as Styled from './styles';

let drawerCounter = 0;

const Drawer = ({
  open,
  onClose,
  title,
  subtitle,
  footer,
  children,
  className,
  side,
  topOffset,
  leftOffset,
  disableAnimation,
  elevateNav,
}) => {
  const idRef = useRef(null);
  if (idRef.current === null) {
    drawerCounter += 1;
    idRef.current = drawerCounter;
  }
  const titleId = `drawer-title-${idRef.current}`;
  const subtitleId = `drawer-subtitle-${idRef.current}`;
  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.classList.add('modal-open');
    // Lets the new-design nav rail (and its hover tooltips) sit above this docked drawer.
    if (elevateNav) document.body.classList.add('nav-rail-elevated');

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.classList.remove('modal-open');
      document.body.classList.remove('nav-rail-elevated');
    };
  }, [open, onClose, elevateNav]);

  return ReactDOM.createPortal(
    <Transition in={open} timeout={300} unmountOnExit mountOnEnter>
      {(state) => (
        <FocusTrap focusTrapOptions={{ clickOutsideDeactivates: true }}>
          <div>
            <Styled.Overlay
              state={state}
              topOffset={topOffset}
              leftOffset={leftOffset}
              disableAnimation={disableAnimation}
              onClick={onClose}
              aria-hidden="true"
            />
            <Styled.Panel
              state={state}
              side={side}
              topOffset={topOffset}
              leftOffset={leftOffset}
              disableAnimation={disableAnimation}
              role="dialog"
              aria-modal="true"
              aria-labelledby={title ? titleId : undefined}
              aria-describedby={subtitle ? subtitleId : undefined}
              className={className}
            >
              <Styled.Header>
                <Styled.HeaderText>
                  {title ? <Styled.Title id={titleId}>{title}</Styled.Title> : null}
                  {subtitle ? <Styled.Subtitle id={subtitleId}>{subtitle}</Styled.Subtitle> : null}
                </Styled.HeaderText>
                <Styled.CloseButton type="button" onClick={onClose} aria-label="Close" />
              </Styled.Header>
              <Styled.Content>{children}</Styled.Content>
              {footer ? <Styled.Footer>{footer}</Styled.Footer> : null}
            </Styled.Panel>
          </div>
        </FocusTrap>
      )}
    </Transition>,
    document.body,
  );
};

Drawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  footer: PropTypes.node,
  children: PropTypes.node,
  className: PropTypes.string,
  side: PropTypes.oneOf(['left', 'right']),
  topOffset: PropTypes.number,
  leftOffset: PropTypes.number,
  disableAnimation: PropTypes.bool,
  elevateNav: PropTypes.bool,
};

Drawer.defaultProps = {
  title: '',
  subtitle: '',
  footer: null,
  children: null,
  className: '',
  side: 'right',
  topOffset: 0,
  leftOffset: 0,
  disableAnimation: false,
  elevateNav: false,
};

export default Drawer;
